import React, { useState, useEffect, useCallback } from 'react';
import { getApiUrl } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from './Notification';
import { theme } from '../theme';
import { Player, PlayerReview, AvailableTags, CustomTag } from '../types';
import { TAG_EMOJIS, TAG_LIMITS } from '../constants/reviewConstants';
import { CreateCustomTagModal } from './CreateCustomTagModal';
import { logger } from '../utils/logger';
import { useResponsive } from '../hooks/useResponsive';

interface CreateReviewProps {
  matchId: string;
  reviewedPlayer?: Player;
  onReviewCreated: (review: PlayerReview) => void;
  onCancel: () => void;
  isVisible: boolean;
}

type WizardStep = 'tags' | 'details' | 'confirm';

export const CreateReview: React.FC<CreateReviewProps> = ({
  matchId,
  reviewedPlayer,
  onReviewCreated,
  onCancel,
  isVisible
}) => {
  const { user } = useAuth();
  const { isMobile } = useResponsive();
  const { notification, showNotification, hideNotification } = useNotification();
  
  // Wizard state (3 steps: tags → details → confirm, like mobile)
  const [currentStep, setCurrentStep] = useState<WizardStep>('tags');
  
  // Form state
  const [comment, setComment] = useState('');
  const [selectedPositiveTags, setSelectedPositiveTags] = useState<string[]>([]);
  const [selectedNegativeTags, setSelectedNegativeTags] = useState<string[]>([]);
  const [selectedCustomPositiveTags, setSelectedCustomPositiveTags] = useState<string[]>([]);
  const [selectedCustomNegativeTags, setSelectedCustomNegativeTags] = useState<string[]>([]);
  const [showCreateTagModal, setShowCreateTagModal] = useState(false);
  const [availableTags, setAvailableTags] = useState<AvailableTags>({ 
    predefined: { positive: [], negative: [] }, 
    custom: { positive: [], negative: [] } 
  });
  const [loading, setLoading] = useState(false);
  const [loadingTags, setLoadingTags] = useState(true);
  const [isMVP, setIsMVP] = useState(false);
  const [hasVoted, setHasVoted] = useState(false);
  const [votedPlayer, setVotedPlayer] = useState<{ id: string; name: string } | null>(null);

  const checkVoteStatus = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl(`/api/matches/${matchId}/vote/check`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setHasVoted(data.hasVoted);
        setVotedPlayer(data.vote ? { id: data.vote.votedPlayerId, name: data.vote.votedPlayerName } : null);
      }
    } catch (error) {
      logger.error('Error checking vote status:', error);
    }
  }, [user?.id, matchId]);

  const fetchAvailableTags = useCallback(async () => {
    try {
      const response = await fetch(getApiUrl('/api/reviews/tags'));
      if (response.ok) {
        const tags: AvailableTags = await response.json();
        setAvailableTags(tags);
      } else {
        showNotification('Error cargando etiquetas disponibles', 'error');
      }
    } catch (error) {
      logger.error('Error fetching tags:', error);
      showNotification('Error cargando etiquetas', 'error');
    } finally {
      setLoadingTags(false);
    }
  }, [showNotification]);

  useEffect(() => {
    fetchAvailableTags();
    if (isVisible) checkVoteStatus();
  }, [isVisible, fetchAvailableTags, checkVoteStatus]);



  const handlePositiveTagToggle = (tag: string) => {
    setSelectedPositiveTags(prev => {
      if (prev.includes(tag)) {
        return prev.filter(t => t !== tag);
      } else {
        if (prev.length >= TAG_LIMITS.PREDEFINED_MAX) {
          showNotification(`Solo puedes seleccionar máximo ${TAG_LIMITS.PREDEFINED_MAX} etiquetas positivas`, 'warning');
          return prev;
        }
        return [...prev, tag];
      }
    });
  };

  const handleNegativeTagToggle = (tag: string) => {
    setSelectedNegativeTags(prev => {
      if (prev.includes(tag)) {
        return prev.filter(t => t !== tag);
      } else {
        if (prev.length >= TAG_LIMITS.PREDEFINED_MAX) {
          showNotification(`Solo puedes seleccionar máximo ${TAG_LIMITS.PREDEFINED_MAX} etiquetas negativas`, 'warning');
          return prev;
        }
        return [...prev, tag];
      }
    });
  };

  const handleCustomPositiveTagToggle = (tag: string) => {
    setSelectedCustomPositiveTags(prev => {
      if (prev.includes(tag)) {
        return prev.filter(t => t !== tag);
      } else {
        if (prev.length >= TAG_LIMITS.CUSTOM_MAX) {
          showNotification(`Solo puedes seleccionar máximo ${TAG_LIMITS.CUSTOM_MAX} etiquetas personalizadas positivas`, 'warning');
          return prev;
        }
        return [...prev, tag];
      }
    });
  };

  const handleCustomNegativeTagToggle = (tag: string) => {
    setSelectedCustomNegativeTags(prev => {
      if (prev.includes(tag)) {
        return prev.filter(t => t !== tag);
      } else {
        if (prev.length >= TAG_LIMITS.CUSTOM_MAX) {
          showNotification(`Solo puedes seleccionar máximo ${TAG_LIMITS.CUSTOM_MAX} etiquetas personalizadas negativas`, 'warning');
          return prev;
        }
        return [...prev, tag];
      }
    });
  };

  const handleCustomTagCreated = (tag: CustomTag) => {
    // Una sola actualización: agregar el tag a la lista (optimista) y a la selección. Sin refetch después
    // para evitar que la lista "salte" o se reordene cuando llegue la respuesta del servidor.
    const item = { name: tag.name, usageCount: tag.usageCount ?? 1 };
    setAvailableTags(prev => {
      const alreadyInPositive = prev.custom.positive.some(t => t.name.toLowerCase() === tag.name.toLowerCase());
      const alreadyInNegative = prev.custom.negative.some(t => t.name.toLowerCase() === tag.name.toLowerCase());
      return {
        ...prev,
        custom: {
          positive: tag.type === 'POSITIVE' && !alreadyInPositive ? [item, ...prev.custom.positive] : prev.custom.positive,
          negative: tag.type === 'NEGATIVE' && !alreadyInNegative ? [item, ...prev.custom.negative] : prev.custom.negative
        }
      };
    });
    if (tag.type === 'POSITIVE') {
      handleCustomPositiveTagToggle(tag.name);
    } else {
      handleCustomNegativeTagToggle(tag.name);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!reviewedPlayer) {
      showNotification('No se seleccionó jugador para review', 'error');
      return;
    }
    
    // El comentario es opcional, no se valida aquí

    if (selectedPositiveTags.length + selectedNegativeTags.length + selectedCustomPositiveTags.length + selectedCustomNegativeTags.length === 0) {
      showNotification('Debes seleccionar al menos una etiqueta (positiva o negativa)', 'error');
      return;
    }

    if (isMVP && hasVoted) {
      showNotification('Ya votaste por un MVP en este partido', 'error');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      
      // Crear la review
      const reviewResponse = await fetch(getApiUrl('/api/reviews'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          matchId,
          reviewedPlayerId: reviewedPlayer.id,
          comment: comment.trim() || '',
  
          positiveTags: selectedPositiveTags,
          negativeTags: selectedNegativeTags,
          customPositiveTags: selectedCustomPositiveTags,
          customNegativeTags: selectedCustomNegativeTags
        })
      });

      if (reviewResponse.ok) {
        const data = await reviewResponse.json();
        
        // Si se marcó como MVP, hacer el voto
        if (isMVP) {
          const voteResponse = await fetch(getApiUrl(`/api/matches/${matchId}/vote`), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ votedPlayerId: reviewedPlayer.id })
          });

          if (voteResponse.ok) {
            showNotification('Review creada y voto de MVP registrado exitosamente', 'success');
            // Actualizar estado de voto
            setHasVoted(true);
            setVotedPlayer({ id: reviewedPlayer.id, name: reviewedPlayer.name });
          } else {
            const voteError = await voteResponse.json();
            showNotification(`Review creada pero error en voto MVP: ${voteError.error}`, 'warning');
          }
        } else {
          showNotification('Review creada exitosamente', 'success');
        }
        
        onReviewCreated(data.review);
        setComment('');
        setSelectedPositiveTags([]);
        setSelectedNegativeTags([]);
        setSelectedCustomPositiveTags([]);
        setSelectedCustomNegativeTags([]);
        setIsMVP(false);
      } else {
        const error = await reviewResponse.json();
        showNotification(error.error || 'Error creando review', 'error');
      }
    } catch (error) {
      logger.error('Error creating review:', error);
      showNotification('Error de conexión', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getStepTitle = (step: WizardStep): string => {
    switch (step) {
      case 'tags': return 'Etiquetas';
      case 'details': return 'Comentario y MVP';
      case 'confirm': return 'Confirma tu review';
      default: return '';
    }
  };

  const getStepDescription = (step: WizardStep): string => {
    switch (step) {
      case 'tags': return 'Seleccioná las etiquetas que mejor describan su desempeño';
      case 'details': return 'Comentario opcional y voto MVP';
      case 'confirm': return 'Revisá y confirmá antes de enviar';
      default: return '';
    }
  };

  const canGoToNextStep = (): boolean => {
    switch (currentStep) {
      case 'tags':
        return selectedPositiveTags.length + selectedNegativeTags.length + selectedCustomPositiveTags.length + selectedCustomNegativeTags.length > 0;
      case 'details':
        return true;
      case 'confirm':
        return false;
      default:
        return false;
    }
  };

  const canGoToPreviousStep = (): boolean => {
    return currentStep !== 'tags';
  };

  const handleNextStep = () => {
    if (currentStep === 'tags') setCurrentStep('details');
    else if (currentStep === 'details') setCurrentStep('confirm');
  };

  const handlePreviousStep = () => {
    if (currentStep === 'details') setCurrentStep('tags');
    else if (currentStep === 'confirm') setCurrentStep('details');
  };

  const getProgressPercentage = (): number => {
    const steps: WizardStep[] = ['tags', 'details', 'confirm'];
    const currentIndex = steps.indexOf(currentStep);
    return ((currentIndex + 1) / steps.length) * 100;
  };

  if (!isVisible) return null;
  if (!reviewedPlayer) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: isMobile ? '16px' : '32px'
    }}>
      <div style={{
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.card,
        padding: isMobile ? '16px' : theme.spacing.lg,
        maxWidth: isMobile ? '100%' : '600px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'hidden', // Changed from 'auto' to 'hidden'
        boxShadow: theme.colors.shadow,
        border: `2px solid ${theme.colors.primary}`,
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header with progress bar */}
        <div style={{
          borderBottom: `1px solid ${theme.colors.border}`,
          paddingBottom: theme.spacing.md,
          marginBottom: theme.spacing.md
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: theme.spacing.sm
          }}>
            <h2 style={{
              margin: 0,
              color: theme.colors.primary,
              fontFamily: theme.fonts.heading,
              fontSize: isMobile ? '18px' : theme.fonts.sizeTitle
            }}>
              {getStepTitle(currentStep)}
            </h2>
            <button
              onClick={onCancel}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: theme.colors.textSecondary
              }}
            >
              ×
            </button>
          </div>
          
          {/* Progress bar */}
          <div style={{
            width: '100%',
            height: '4px',
            backgroundColor: theme.colors.border,
            borderRadius: '2px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${getProgressPercentage()}%`,
              height: '100%',
              backgroundColor: theme.colors.primary,
              transition: 'width 0.3s ease'
            }} />
          </div>
          
          <p style={{
            margin: '8px 0 0 0',
            color: theme.colors.textSecondary,
            fontSize: theme.fonts.sizeSmall,
            textAlign: 'center'
          }}>
            {getStepDescription(currentStep)}
          </p>
        </div>

        {/* Step content */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          padding: '0 4px'
        }}>
          {currentStep === 'tags' && (
            <TagsSelectionStep
              availableTags={availableTags}
              selectedPositiveTags={selectedPositiveTags}
              selectedNegativeTags={selectedNegativeTags}
              selectedCustomPositiveTags={selectedCustomPositiveTags}
              selectedCustomNegativeTags={selectedCustomNegativeTags}
              handlePositiveTagToggle={handlePositiveTagToggle}
              handleNegativeTagToggle={handleNegativeTagToggle}
              handleCustomPositiveTagToggle={handleCustomPositiveTagToggle}
              handleCustomNegativeTagToggle={handleCustomNegativeTagToggle}
              setShowCreateTagModal={setShowCreateTagModal}
              loadingTags={loadingTags}
            />
          )}

          {currentStep === 'details' && (
            <DetailsStep
              comment={comment}
              setComment={setComment}
              isMVP={isMVP}
              setIsMVP={setIsMVP}
              hasVoted={hasVoted}
              votedPlayer={votedPlayer}
              reviewedPlayer={reviewedPlayer}
            />
          )}

          {currentStep === 'confirm' && (
            <ConfirmationStep
              reviewedPlayer={reviewedPlayer}
              selectedPositiveTags={selectedPositiveTags}
              selectedNegativeTags={selectedNegativeTags}
              selectedCustomPositiveTags={selectedCustomPositiveTags}
              selectedCustomNegativeTags={selectedCustomNegativeTags}
              comment={comment}
              isMVP={isMVP}
            />
          )}
        </div>

        {/* Navigation buttons */}
        <div style={{
          display: 'flex',
          gap: theme.spacing.sm,
          justifyContent: 'space-between',
          paddingTop: theme.spacing.md,
          borderTop: `1px solid ${theme.colors.border}`
        }}>
          <button
            type="button"
            onClick={handlePreviousStep}
            disabled={!canGoToPreviousStep()}
            style={{
              padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
              backgroundColor: 'transparent',
              color: theme.colors.textSecondary,
              border: `2px solid ${theme.colors.border}`,
              borderRadius: theme.borderRadius.button,
              fontFamily: theme.fonts.body,
              fontSize: theme.fonts.sizeBody,
              cursor: canGoToPreviousStep() ? 'pointer' : 'not-allowed',
              opacity: canGoToPreviousStep() ? 1 : 0.6
            }}
          >
            ← Anterior
          </button>

          {currentStep === 'confirm' ? (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              style={{
                padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
                backgroundColor: theme.colors.success,
                color: theme.colors.white,
                border: 'none',
                borderRadius: theme.borderRadius.button,
                fontFamily: theme.fonts.body,
                fontSize: theme.fonts.sizeBody,
                fontWeight: theme.fonts.weightBold,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1
              }}
            >
              {loading ? 'Creando...' : '✅ Crear Review'}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleNextStep}
              disabled={!canGoToNextStep()}
              style={{
                padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
                backgroundColor: theme.colors.primary,
                color: theme.colors.white,
                border: 'none',
                borderRadius: theme.borderRadius.button,
                fontFamily: theme.fonts.body,
                fontSize: theme.fonts.sizeBody,
                fontWeight: theme.fonts.weightBold,
                cursor: canGoToNextStep() ? 'pointer' : 'not-allowed',
                opacity: canGoToNextStep() ? 1 : 0.6
              }}
            >
              Siguiente →
            </button>
          )}
        </div>

        {/* Notificación */}
        {notification.isVisible && (
          <button type="button" onClick={hideNotification} style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            padding: '16px 20px',
            borderRadius: '8px',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '14px',
            zIndex: 1001,
            maxWidth: '400px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            cursor: 'pointer',
            border: 'none',
            backgroundColor: notification.type === 'success' ? theme.colors.success :
                           notification.type === 'error' ? theme.colors.danger :
                           notification.type === 'warning' ? theme.colors.warning : theme.colors.primary
          }}>
            <span>{notification.type === 'success' ? '✅' : notification.type === 'error' ? '❌' : notification.type === 'warning' ? '⚠️' : 'ℹ️'}</span>
            <span>{notification.message}</span>
          </button>
        )}

        {/* Modal para crear tags personalizados */}
        <CreateCustomTagModal
          isVisible={showCreateTagModal}
          onClose={() => setShowCreateTagModal(false)}
          onTagCreated={handleCustomTagCreated}
          playerPosition={reviewedPlayer?.position}
        />
      </div>
    </div>
  );
};

// Step Components
type TagTab = 'positive' | 'negative' | 'custom';

const TagsSelectionStep: React.FC<{
  availableTags: AvailableTags;
  selectedPositiveTags: string[];
  selectedNegativeTags: string[];
  selectedCustomPositiveTags: string[];
  selectedCustomNegativeTags: string[];
  handlePositiveTagToggle: (tag: string) => void;
  handleNegativeTagToggle: (tag: string) => void;
  handleCustomPositiveTagToggle: (tag: string) => void;
  handleCustomNegativeTagToggle: (tag: string) => void;
  setShowCreateTagModal: (show: boolean) => void;
  loadingTags: boolean;
}> = (props) => {
  const {
    availableTags,
    selectedPositiveTags,
    selectedNegativeTags,
    selectedCustomPositiveTags,
    selectedCustomNegativeTags,
    handlePositiveTagToggle,
    handleNegativeTagToggle,
    handleCustomPositiveTagToggle,
    handleCustomNegativeTagToggle,
    setShowCreateTagModal,
    loadingTags
  } = props;
  const [currentTab, setCurrentTab] = useState<TagTab>('positive');

  const positiveCount = selectedPositiveTags.length;
  const negativeCount = selectedNegativeTags.length;
  const customCount = selectedCustomPositiveTags.length + selectedCustomNegativeTags.length;

  if (loadingTags) {
    return (
      <div style={{ textAlign: 'center', padding: theme.spacing.lg }}>
        Cargando etiquetas...
      </div>
    );
  }

  const tabStyle = (tab: TagTab) => ({
    padding: '8px 16px',
    border: 'none',
    borderRadius: theme.borderRadius.button,
    fontFamily: theme.fonts.body,
    fontSize: theme.fonts.sizeSmall,
    fontWeight: 600,
    cursor: 'pointer',
    backgroundColor: currentTab === tab ? theme.colors.primary : 'transparent',
    color: currentTab === tab ? theme.colors.background : theme.colors.textSecondary,
    borderBottom: currentTab === tab ? `2px solid ${theme.colors.primary}` : '2px solid transparent'
  } as const);

  return (
    <div>
      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: theme.spacing.md, flexWrap: 'wrap' }}>
        <button type="button" style={tabStyle('positive')} onClick={() => setCurrentTab('positive')}>
          ✅ Positivas {positiveCount > 0 && `(${positiveCount}/3)`}
        </button>
        <button type="button" style={tabStyle('negative')} onClick={() => setCurrentTab('negative')}>
          ❌ Negativas {negativeCount > 0 && `(${negativeCount}/3)`}
        </button>
        <button type="button" style={tabStyle('custom')} onClick={() => setCurrentTab('custom')}>
          🏷️ Custom {customCount > 0 && `(${customCount})`}
        </button>
      </div>

      {currentTab === 'positive' && (
        <div style={{ marginBottom: theme.spacing.lg }}>
          <div style={{ marginBottom: 8, color: theme.colors.textSecondary, fontSize: theme.fonts.sizeSmall }}>
            Máximo 3 etiquetas positivas
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {availableTags.predefined.positive.map((tag: string) => {
              const emoji = TAG_EMOJIS[tag] || '';
              const isSelected = selectedPositiveTags.includes(tag);
              return (
                <button
                  key={tag}
                  type="button"
                  onClick={() => handlePositiveTagToggle(tag)}
                  style={{
                    padding: '8px 12px',
                    border: `2px solid ${isSelected ? theme.colors.success : theme.colors.border}`,
                    borderRadius: theme.borderRadius.button,
                    backgroundColor: isSelected ? theme.colors.success : 'transparent',
                    color: isSelected ? theme.colors.white : theme.colors.success,
                    cursor: 'pointer',
                    fontFamily: theme.fonts.body,
                    fontSize: theme.fonts.sizeSmall,
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                  }}
                >
                  {emoji && <span>{emoji}</span>}
                  {tag}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {currentTab === 'negative' && (
        <div style={{ marginBottom: theme.spacing.lg }}>
          <div style={{ marginBottom: 8, color: theme.colors.textSecondary, fontSize: theme.fonts.sizeSmall }}>
            Máximo 3 etiquetas negativas
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {availableTags.predefined.negative.map((tag: string) => {
              const emoji = TAG_EMOJIS[tag] || '';
              const isSelected = selectedNegativeTags.includes(tag);
              return (
                <button
                  key={tag}
                  type="button"
                  onClick={() => handleNegativeTagToggle(tag)}
                  style={{
                    padding: '8px 12px',
                    border: `2px solid ${isSelected ? theme.colors.danger : theme.colors.border}`,
                    borderRadius: theme.borderRadius.button,
                    backgroundColor: isSelected ? theme.colors.danger : 'transparent',
                    color: isSelected ? theme.colors.white : theme.colors.danger,
                    cursor: 'pointer',
                    fontFamily: theme.fonts.body,
                    fontSize: theme.fonts.sizeSmall,
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                  }}
                >
                  {emoji && <span>{emoji}</span>}
                  {tag}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {currentTab === 'custom' && (
        <div style={{ marginBottom: theme.spacing.lg }}>
          {availableTags.custom.positive.length > 0 && (
            <div style={{ marginBottom: theme.spacing.md }}>
              <h4 style={{ color: theme.colors.success, fontSize: 14, margin: '0 0 8px 0', fontFamily: theme.fonts.body, fontWeight: 600 }}>
                Positivos
              </h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {availableTags.custom.positive.map((tag) => (
                  <button
                    key={tag.name}
                    type="button"
                    onClick={() => handleCustomPositiveTagToggle(tag.name)}
                    style={{
                      padding: '6px 10px',
                      border: `2px solid ${selectedCustomPositiveTags.includes(tag.name) ? theme.colors.success : theme.colors.border}`,
                      borderRadius: 12,
                      backgroundColor: selectedCustomPositiveTags.includes(tag.name) ? theme.colors.success : 'transparent',
                      color: selectedCustomPositiveTags.includes(tag.name) ? theme.colors.white : theme.colors.success,
                      cursor: 'pointer',
                      fontFamily: theme.fonts.body,
                      fontSize: 12,
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {tag.name} ({tag.usageCount})
                  </button>
                ))}
              </div>
            </div>
          )}
          {availableTags.custom.negative.length > 0 && (
            <div style={{ marginBottom: theme.spacing.md }}>
              <h4 style={{ color: theme.colors.danger, fontSize: 14, margin: '0 0 8px 0', fontFamily: theme.fonts.body, fontWeight: 600 }}>
                Negativos
              </h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {availableTags.custom.negative.map((tag) => (
                  <button
                    key={tag.name}
                    type="button"
                    onClick={() => handleCustomNegativeTagToggle(tag.name)}
                    style={{
                      padding: '6px 10px',
                      border: `2px solid ${selectedCustomNegativeTags.includes(tag.name) ? theme.colors.danger : theme.colors.border}`,
                      borderRadius: 12,
                      backgroundColor: selectedCustomNegativeTags.includes(tag.name) ? theme.colors.danger : 'transparent',
                      color: selectedCustomNegativeTags.includes(tag.name) ? theme.colors.white : theme.colors.danger,
                      cursor: 'pointer',
                      fontFamily: theme.fonts.body,
                      fontSize: 12,
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {tag.name} ({tag.usageCount})
                  </button>
                ))}
              </div>
            </div>
          )}
          <button
            type="button"
            onClick={() => setShowCreateTagModal(true)}
            style={{
              padding: '8px 16px',
              backgroundColor: 'transparent',
              border: `2px dashed ${theme.colors.primary}`,
              color: theme.colors.primary,
              borderRadius: theme.borderRadius.button,
              cursor: 'pointer',
              fontSize: 14,
              fontFamily: theme.fonts.body,
              marginTop: 8
            }}
          >
            ➕ Crear nuevo tag personalizado
          </button>
        </div>
      )}

      <div style={{
        fontSize: theme.fonts.sizeSmall,
        color: theme.colors.textSecondary,
        textAlign: 'center',
        padding: theme.spacing.sm,
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderRadius: theme.borderRadius.card
      }}>
        Positivas: {positiveCount}/{TAG_LIMITS.PREDEFINED_MAX} · Negativas: {negativeCount}/{TAG_LIMITS.PREDEFINED_MAX}
        {(customCount > 0) && ` • Custom: ${selectedCustomPositiveTags.length}/${TAG_LIMITS.CUSTOM_MAX} + ${selectedCustomNegativeTags.length}/${TAG_LIMITS.CUSTOM_MAX}`}
      </div>
    </div>
  );
};

const DetailsStep: React.FC<{
  comment: string;
  setComment: (comment: string) => void;
  isMVP: boolean;
  setIsMVP: (isMVP: boolean) => void;
  hasVoted: boolean;
  votedPlayer: { id: string; name: string } | null;
  reviewedPlayer: Player | null;
}> = ({ comment, setComment, isMVP, setIsMVP, hasVoted, votedPlayer, reviewedPlayer }) => {
  const rating = reviewedPlayer?.rating ?? 0;
  const fullStars = Math.min(5, Math.floor(rating));
  const starsDisplay = '⭐'.repeat(fullStars) + '☆'.repeat(5 - fullStars);

  return (
    <div>
      {/* Rating actual del jugador */}
      {reviewedPlayer && (
        <div style={{
          marginBottom: theme.spacing.lg,
          padding: theme.spacing.md,
          backgroundColor: 'rgba(255, 215, 0, 0.08)',
          borderRadius: theme.borderRadius.card,
          border: '1px solid rgba(255, 215, 0, 0.3)'
        }}>
          <div style={{ color: theme.colors.textSecondary, fontSize: theme.fonts.sizeSmall, marginBottom: 4 }}>
            Rating actual de {reviewedPlayer.name}
          </div>
          <div style={{ fontSize: '1.2rem', letterSpacing: 2 }}>
            {starsDisplay} <span style={{ fontWeight: theme.fonts.weightBold, color: theme.colors.text, marginLeft: 8 }}>{rating.toFixed(1)}</span>
          </div>
        </div>
      )}

      {/* Comentario */}
      <div style={{ marginBottom: theme.spacing.lg }}>
        <label htmlFor="create-review-comment" style={{
          display: 'block',
          marginBottom: theme.spacing.sm,
          color: theme.colors.text,
          fontFamily: theme.fonts.body,
          fontWeight: theme.fonts.weightBold
        }}>
          Comentario (opcional, máximo 100 caracteres)
        </label>
        <textarea
          id="create-review-comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          maxLength={100}
          style={{
            width: '100%',
            minHeight: '80px',
            padding: theme.spacing.sm,
            border: `2px solid ${theme.colors.border}`,
            borderRadius: theme.borderRadius.input,
            fontFamily: theme.fonts.body,
            fontSize: theme.fonts.sizeBody,
            resize: 'vertical'
          }}
          placeholder="Escribe tu comentario sobre el desempeño del jugador..."
        />
        <div style={{
          textAlign: 'right',
          fontSize: theme.fonts.sizeSmall,
          color: theme.colors.textSecondary,
          marginTop: '4px'
        }}>
          {comment.length}/100
        </div>
      </div>

      {/* Checkbox MVP */}
      <div style={{ marginBottom: theme.spacing.lg }}>
        {hasVoted ? (
          <div style={{
            padding: theme.spacing.md,
            backgroundColor: 'rgba(255, 107, 107, 0.1)',
            borderRadius: theme.borderRadius.card,
            border: '2px solid #FF6B6B',
            opacity: 0.7
          }}>
            <div style={{
              color: '#FF6B6B',
              fontFamily: theme.fonts.body,
              fontSize: theme.fonts.sizeBody,
              fontWeight: theme.fonts.weightBold,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              👑 Ya votaste como MVP
            </div>
            <div style={{
              color: theme.colors.textSecondary,
              fontSize: theme.fonts.sizeSmall,
              marginTop: '4px'
            }}>
              Ya votaste por {votedPlayer?.name || 'otro jugador'} como MVP en este partido
            </div>
          </div>
        ) : (
          <label aria-label="Votar como MVP" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            cursor: 'pointer',
            padding: theme.spacing.md,
            backgroundColor: 'rgba(255, 215, 0, 0.1)',
            borderRadius: theme.borderRadius.card,
            border: `2px solid ${isMVP ? '#FFD700' : 'rgba(255, 215, 0, 0.3)'}`,
            transition: 'all 0.2s ease'
          }}>
            <input
              type="checkbox"
              checked={isMVP}
              onChange={(e) => setIsMVP(e.target.checked)}
              style={{
                width: '20px',
                height: '20px',
                accentColor: '#FFD700',
                cursor: 'pointer'
              }}
            />
            <div>
              <div style={{
                color: '#FFD700',
                fontFamily: theme.fonts.body,
                fontSize: theme.fonts.sizeBody,
                fontWeight: theme.fonts.weightBold,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                👑 Votar como Mejor Jugador (MVP)
              </div>
              <div style={{
                color: theme.colors.textSecondary,
                fontSize: theme.fonts.sizeSmall,
                marginTop: '4px'
              }}>
                Solo puedes votar por un jugador como MVP por partido
              </div>
            </div>
          </label>
        )}
      </div>
    </div>
  );
};

const ConfirmationStep: React.FC<{
  reviewedPlayer: Player;
  selectedPositiveTags: string[];
  selectedNegativeTags: string[];
  selectedCustomPositiveTags: string[];
  selectedCustomNegativeTags: string[];
  comment: string;
  isMVP: boolean;
}> = ({
  reviewedPlayer,
  selectedPositiveTags,
  selectedNegativeTags,
  selectedCustomPositiveTags,
  selectedCustomNegativeTags,
  comment,
  isMVP
}) => {
  return (
    <div>
      <div style={{
        backgroundColor: 'rgba(0, 230, 118, 0.1)',
        border: `1px solid ${theme.colors.success}`,
        borderRadius: theme.borderRadius.card,
        padding: theme.spacing.md,
        marginBottom: theme.spacing.lg,
        textAlign: 'center'
      }}>
        <div style={{
          color: theme.colors.success,
          fontFamily: theme.fonts.body,
          fontSize: theme.fonts.sizeBody,
          fontWeight: theme.fonts.weightBold,
          marginBottom: '4px'
        }}>
          ✨ Review lista para enviar
        </div>
        <div style={{
          color: theme.colors.textSecondary,
          fontSize: theme.fonts.sizeSmall
        }}>
          Revisá los detalles antes de confirmar
        </div>
      </div>

      {/* Resumen del jugador */}
      <div style={{
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderRadius: theme.borderRadius.card,
        padding: theme.spacing.md,
        marginBottom: theme.spacing.lg,
        textAlign: 'center'
      }}>
        <div style={{
          color: theme.colors.primary,
          fontFamily: theme.fonts.body,
          fontSize: theme.fonts.sizeBody,
          fontWeight: theme.fonts.weightBold,
          marginBottom: '4px'
        }}>
          👤 Jugador a reviewear
        </div>
        <div style={{
          color: theme.colors.text,
          fontSize: theme.fonts.sizeBody
        }}>
          {reviewedPlayer.name}
          {reviewedPlayer.position && ` • ${reviewedPlayer.position}`}
        </div>
      </div>

      {/* Resumen de etiquetas */}
      <div style={{
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderRadius: theme.borderRadius.card,
        padding: theme.spacing.md,
        marginBottom: theme.spacing.lg
      }}>
        <div style={{
          color: theme.colors.text,
          fontFamily: theme.fonts.body,
          fontSize: theme.fonts.sizeBody,
          fontWeight: theme.fonts.weightBold,
          marginBottom: theme.spacing.sm,
          textAlign: 'center'
        }}>
          🏷️ Etiquetas seleccionadas
        </div>
        
        {selectedPositiveTags.length > 0 && (
          <div style={{ marginBottom: theme.spacing.sm }}>
            <div style={{
              color: theme.colors.success,
              fontSize: theme.fonts.sizeSmall,
              fontWeight: theme.fonts.weightBold,
              marginBottom: '4px'
            }}>
              ✅ Positivas ({selectedPositiveTags.length})
            </div>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '4px'
            }}>
              {selectedPositiveTags.map(tag => (
                <span
                  key={tag}
                  style={{
                    backgroundColor: theme.colors.success,
                    color: theme.colors.white,
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px'
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {selectedNegativeTags.length > 0 && (
          <div style={{ marginBottom: theme.spacing.sm }}>
            <div style={{
              color: theme.colors.danger,
              fontSize: theme.fonts.sizeSmall,
              fontWeight: theme.fonts.weightBold,
              marginBottom: '4px'
            }}>
              ❌ Negativas ({selectedNegativeTags.length})
            </div>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '4px'
            }}>
              {selectedNegativeTags.map(tag => (
                <span
                  key={tag}
                  style={{
                    backgroundColor: theme.colors.danger,
                    color: theme.colors.white,
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px'
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {selectedCustomPositiveTags.length > 0 && (
          <div style={{ marginBottom: theme.spacing.sm }}>
            <div style={{
              color: theme.colors.primary,
              fontSize: theme.fonts.sizeSmall,
              fontWeight: theme.fonts.weightBold,
              marginBottom: '4px'
            }}>
              🏷️ Personalizadas Positivas ({selectedCustomPositiveTags.length})
            </div>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '4px'
            }}>
              {selectedCustomPositiveTags.map(tag => (
                <span
                  key={tag}
                  style={{
                    backgroundColor: theme.colors.primary,
                    color: theme.colors.white,
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px'
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {selectedCustomNegativeTags.length > 0 && (
          <div style={{ marginBottom: theme.spacing.sm }}>
            <div style={{
              color: theme.colors.primary,
              fontSize: theme.fonts.sizeSmall,
              fontWeight: theme.fonts.weightBold,
              marginBottom: '4px'
            }}>
              🏷️ Personalizadas Negativas ({selectedCustomNegativeTags.length})
            </div>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '4px'
            }}>
              {selectedCustomNegativeTags.map(tag => (
                <span
                  key={tag}
                  style={{
                    backgroundColor: theme.colors.primary,
                    color: theme.colors.white,
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px'
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Comentario */}
      {comment && (
        <div style={{
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          borderRadius: theme.borderRadius.card,
          padding: theme.spacing.md,
          marginBottom: theme.spacing.lg
        }}>
          <div style={{
            color: theme.colors.text,
            fontFamily: theme.fonts.body,
            fontSize: theme.fonts.sizeBody,
            fontWeight: theme.fonts.weightBold,
            marginBottom: '4px'
          }}>
            💬 Comentario
          </div>
          <div style={{
            color: theme.colors.textSecondary,
            fontStyle: 'italic',
            fontSize: theme.fonts.sizeSmall
          }}>
            "{comment}"
          </div>
        </div>
      )}

      {/* MVP */}
      {isMVP && (
        <div style={{
          backgroundColor: 'rgba(255, 215, 0, 0.1)',
          border: `1px solid #FFD700`,
          borderRadius: theme.borderRadius.card,
          padding: theme.spacing.md,
          textAlign: 'center'
        }}>
          <div style={{
            color: '#FFD700',
            fontFamily: theme.fonts.body,
            fontSize: theme.fonts.sizeBody,
            fontWeight: theme.fonts.weightBold,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}>
            👑 Voto MVP incluido
          </div>
        </div>
      )}
    </div>
  );
}; 